'use client';

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

const TIP_WIDTH = 260;

/** Hover is only an opener where a pointer can actually hover: a tap fires
 *  mouseenter too, and letting it through would open then immediately re-close. */
const canHover = () =>
  typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

/**
 * An explanation on hover, tap or keyboard. Renders a question mark by default,
 * or the children themselves as the target (a table header, a status icon).
 *
 * With no children the trigger is a real focusable button with an accessible
 * name — several of these live in filter Sheets that only exist below `md`,
 * where a hover-only tip would be the one explanation of a metric that can
 * never be opened. With children the wrapper stays a plain span: the consumer
 * already owns the semantics, and some nest this inside their own button.
 *
 * The tip is portaled to <body>, so no table, panel or modal can clip it.
 */
export function InfoTip({ text, children, className }: { text: ReactNode; children?: ReactNode; className?: string }) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const tipId = useId();
  const open = anchor !== null;
  const flip = anchor !== null && anchor.right + 8 + TIP_WIDTH > window.innerWidth;

  const show = () => setAnchor(triggerRef.current?.getBoundingClientRect() ?? null);
  const hide = () => setAnchor(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!triggerRef.current?.contains(event.target as Node)) hide();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const shared = {
    'aria-describedby': open ? tipId : undefined,
    onMouseEnter: () => {
      if (canHover()) show();
    },
    onMouseLeave: () => {
      if (canHover()) hide();
    },
    onClick: () => (open ? hide() : show()),
  };

  const tip = open
    ? createPortal(
        <span
          id={tipId}
          role="tooltip"
          style={{
            position: 'fixed',
            top: anchor.top + anchor.height / 2,
            ...(flip ? { right: window.innerWidth - anchor.left + 8 } : { left: anchor.right + 8 }),
            maxWidth: TIP_WIDTH,
            zIndex: 70,
          }}
          className="corner-brand-xs pointer-events-none w-max -translate-y-1/2 border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-xs font-normal whitespace-normal text-zinc-200 no-underline shadow-lg"
        >
          {text}
        </span>,
        document.body
      )
    : null;

  if (children) {
    return (
      <span
        ref={(el) => {
          triggerRef.current = el;
        }}
        className={cn(
          'inline-flex cursor-help align-middle underline decoration-zinc-600 decoration-dotted underline-offset-4',
          className
        )}
        {...shared}
      >
        {children}
        {tip}
      </span>
    );
  }

  return (
    <button
      ref={(el) => {
        triggerRef.current = el;
      }}
      type="button"
      aria-label={typeof text === 'string' ? text : 'More information'}
      aria-expanded={open}
      className={cn('inline-flex cursor-help align-middle', className)}
      {...shared}
    >
      <HelpCircle className="size-3.5 text-zinc-500 transition-colors hover:text-zinc-300" aria-hidden />
      {tip}
    </button>
  );
}
